import React, { useState, useEffect } from "react";
import styled from "styled-components";
import { useNavigate } from "react-router-dom";
import { useSelector, shallowEqual, useDispatch } from "react-redux";
import { CLEAR_SLACK_SCHEDULER } from "constants/actions";
import ReactSelect from "react-select";

const SlackSchedulerForm = ({ initialData = {}, onSubmit, editId }) => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const [formData, setFormData] = useState(initialData);

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [botList, setBotList] = useState([]);

  const {
    get_auth,
    get_organizations,
    get_employee,
    get_employee_slack,
    get_slack_scheduler,
  } = useSelector(
    (state) => ({
      get_errors: state.errors,
      get_auth: state.auth,
      get_organizations: state.organizations,
      get_employee: state.employees,
      get_employee_slack: state.employee_slack,
      get_slack_scheduler: state.slack_scheduler,
    }),
    shallowEqual
  );

  useEffect(() => {
    if (get_slack_scheduler?.updated) {
      // Reset the created information
      dispatch({ type: CLEAR_SLACK_SCHEDULER });
      setTimeout(() => {
        if (editId) {
          // Redirect to details page
          navigate(`/leader/audit/slack-scheduler/details/${editId}`);
        } else if (get_slack_scheduler?.created?.id) {
          // Redirect to details page
          navigate(
            `/leader/audit/slack-scheduler/details//${get_slack_scheduler.created.id}`
          );
        } else {
          navigate(`/leader/audit/slack-scheduler`);
        }
      }, 1000);
    }
  }, [get_slack_scheduler, navigate, dispatch, editId]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData({
      ...formData,
      [name]: value,
      organization: get_employee?.userEmp?.organization,
    });
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    onSubmit(formData);
  };
  
  useEffect(() => {
    setFormData(initialData);
  }, [initialData]);

  useEffect(() => {
    setBotList(
      get_slack_scheduler?.slack_bot_list.map((bot) => ({
        value: bot.id,
        label: bot.name,
      }))
    );
  }, [get_slack_scheduler]);

  return (
    <FormContainer>
      <form onSubmit={handleSubmit}>
        <FormGroup>
          <Label htmlFor="name">Name</Label>
          <Input
            type="text"
            name="name"
            value={formData.name}
            onChange={handleChange}
            required
          />
        </FormGroup>
        <FormGroup>
          <Label htmlFor="status">Status</Label>
          <Select
            name="status"
            value={formData.status}
            onChange={handleChange}
            required
          >
            <option value={1}>Draft</option>
            <option value={2}>Active</option>
            <option value={3}>Paused</option>
            <option value={4}>Error</option>
            <option value={5}>Archived</option>
          </Select>
        </FormGroup>
        <FormGroup>
          <Label htmlFor="slack_bot">Bot</Label>
          <ReactSelect
            name="slack_bot"
            value={botList.find((bot) => bot.value === formData?.slack_bot?.id)}
            onChange={(selectedOption) =>
              handleChange({
                target: { name: "slack_bot", value: selectedOption.id },
              })
            }
            options={botList.map((bot) => ({ value: bot.id, label: bot.name }))}
          />
        </FormGroup>
        <FormGroup>
          <Label htmlFor="schedule_type">Schedule Type</Label>
          <Select
            name="schedule_type"
            value={formData.schedule_type}
            onChange={handleChange}
            required
          >
            <option value="daily">Daily</option>
            <option value="bi_weekly">Bi-Weekly</option>
            <option value="weekly">Weekly</option>
            <option value="monthly">Monthly</option>
            <option value="quarterly">Every 3 Months</option>
            <option value="yearly">Yearly</option>
          </Select>
        </FormGroup>
        <FormGroup>
          <Label htmlFor="alert_time">Alert Time</Label>
          <Input
            type="time"
            name="alert_time"
            value={formData.alert_time}
            onChange={handleChange}
            required
          />
        </FormGroup>
        <FormGroup>
          <Label htmlFor="timezone">Timezone</Label>
          <Input
            type="text"
            name="timezone"
            value={formData.timezone}
            onChange={handleChange}
            required
          />
        </FormGroup>
        <FormGroup>
          <Label htmlFor="additional_data">Additional Data</Label>
          <Input
            type="text"
            name="additional_data"
            value={formData.additional_data}
            onChange={handleChange}
          />
        </FormGroup>
        <Button type="submit" disabled={isSubmitting}>
          Submit
        </Button>
      </form>
    </FormContainer>
  );
};

export default SlackSchedulerForm;

const FormContainer = styled.div`
  max-width: 600px;
  margin: 0 auto;
  padding: 20px;
  border: 1px solid #ccc;
  border-radius: 10px;
`;

const FormGroup = styled.div`
  margin-bottom: 15px;
`;

const Label = styled.label`
  display: block;
  margin-bottom: 5px;
  font-weight: bold;
`;

const Input = styled.input`
  width: 100%;
  padding: 8px;
  box-sizing: border-box;
`;

const Select = styled.select`
  width: 100%;
  padding: 8px;
  box-sizing: border-box;
`;

const Button = styled.button`
  padding: 10px 15px;
  background-color: #007bff;
  color: white;
  border: none;
  border-radius: 5px;
  cursor: pointer;

  &:disabled {
    background-color: #ccc;
  }
`;
